import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { DescriptionOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { OnboardingHeader } from 'modules/offerLetter/components/OfferLetterEmployeeDetail/OnboardingHeader';
import { RejectOfferLetterModal } from 'modules/offerLetter/components/RejectOfferLetter/RejectOfferLetterModal';
import { useOfferLetterStyles } from 'modules/offerLetter/OfferLetterStyles';
import { acceptOfferLetterTerms } from 'modules/offerLetter/store/actions';
import { IOfferLetter, OfferLetterStatusSlug } from 'modules/offerLetter/store/model';
import { selectIsOfferLetterTermsAccepting } from 'modules/offerLetter/store/selectors';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PdfFileError from 'shared/components/PdfViewer/PdfFileError';
import { routes } from 'shared/routes';
import { baseUrl } from 'shared/utils/baseUrl';
import OfferLetterPdf from '../OfferLetterPdf';

export interface IViewOfferStepProps {
    id: string;
    isOnboardedWithAvionte: boolean;
    offerLetter?: IOfferLetter;
}

const basePath = `${baseUrl}/documents/offer_letters`;

export const ViewOfferStep = ({ id, offerLetter, isOnboardedWithAvionte }: IViewOfferStepProps) => {
    const classes = useOfferLetterStyles();
    const dispatch = useDispatch();
    const filePath = `${basePath}/${id}`;

    const isAccepting = useSelector(selectIsOfferLetterTermsAccepting);
    const [isOpenRejectConfirmation, setIsOpenRejectConfirmation] = useState(false);
    const [isIframe, setIsIframe] = useState(true);
    const isActionDisabled = Boolean(offerLetter?.status?.slug !== OfferLetterStatusSlug.Outstanding
        || offerLetter?.offer_letter_terms_accepted_at) || isAccepting;

    const toggleIsIframe = () => {
        setIsIframe(!isIframe);
    };

    const acceptOfferLetter = () => {
        dispatch(acceptOfferLetterTerms.init(id));
    };

    const rejectOfferLetter = () => {
        setIsOpenRejectConfirmation(true);
    };

    return (
        <Box display="flex" flexDirection="column"
            className={classes.fullWidthHeight}
            onClick={toggleIsIframe}
        >
            <OnboardingHeader
                icon={<DescriptionOutlined fontSize="small" className={classes.headerIcon} />}
                title="1. Accept or Reject Offer Letter"
                className={isOnboardedWithAvionte ? classes.blueHeader : undefined}
            />
            <Box className={clsx(classes.contentWrapper, classes.offerWrapper, classes.fullWidthHeight)}>
                {offerLetter ? (
                    <>
                        <OfferLetterPdf
                            title="Offer Letter"
                            id={id}
                            filePath={filePath}
                            className={classes.pdfWrapper}
                            errorRender={(
                                <PdfFileError
                                    className={classes.pdfWrapper}
                                    simple
                                    title="Document generation is in progress."
                                    message="Please try again later."
                                    linkLabel="Go to Offer Letters List"
                                    link={routes.EMPLOYEE_OFFER_LETTER.ROOT}
                                />
                            )}
                        />
                        <Box
                            display="flex"
                            alignItems="center"
                            className={classes.actionButtonsWrapper}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={acceptOfferLetter}
                                disabled={isActionDisabled}
                                data-testid="accept-offer-button"
                            >
                                Accept
                                {isAccepting && (
                                    <Box ml={1} mb={-0.5}>
                                        <CircularProgress color="inherit" size={16} />
                                    </Box>
                                )}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={rejectOfferLetter}
                                disabled={isActionDisabled}
                                data-testid="reject-offer-button"
                            >
                                Reject
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Typography>
                        The link is incorrect. Please, check the email or contact the support.
                    </Typography>
                )}
            </Box>
            <RejectOfferLetterModal
                offerLetterId={id}
                open={isOpenRejectConfirmation}
                onClose={() => setIsOpenRejectConfirmation(false)}
            />
        </Box>
    );
};
